/**
 * Tenant-Aware PWA Manifest Generation
 *
 * Generates a dynamic web app manifest based on tenant branding.
 * Supports cache-busting for tenant config changes.
 */

import type { TenantConfig, BrandConfig } from './types';

// ============================================
// MANIFEST TYPES
// ============================================

interface ManifestIcon {
  src: string;
  sizes: string;
  type: string;
  purpose?: 'any' | 'maskable' | 'any maskable';
}

interface WebAppManifest {
  name: string;
  short_name: string;
  description?: string;
  start_url: string;
  display: 'standalone' | 'fullscreen' | 'minimal-ui' | 'browser';
  orientation?: 'portrait' | 'landscape' | 'any';
  background_color: string;
  theme_color: string;
  icons: ManifestIcon[];
  categories?: string[];
  lang?: string;
  dir?: 'ltr' | 'rtl' | 'auto';
  scope?: string;
  id?: string;
}

// ============================================
// MANIFEST GENERATION
// ============================================

/**
 * Generate a PWA manifest from tenant configuration
 */
export function generateManifest(tenant: TenantConfig): WebAppManifest {
  const { brand } = tenant;

  const icons: ManifestIcon[] = [];

  // Standard icon
  if (brand.logos.icon) {
    icons.push({
      src: brand.logos.icon,
      sizes: '64x64',
      type: 'image/png',
    });
  }

  // 192x192 icon (required for PWA)
  if (brand.logos.icon192) {
    icons.push({
      src: brand.logos.icon192,
      sizes: '192x192',
      type: 'image/png',
    });
  }

  // 512x512 icon (required for PWA)
  if (brand.logos.icon512) {
    icons.push({
      src: brand.logos.icon512,
      sizes: '512x512',
      type: 'image/png',
    });
  }

  // Maskable icon (for Android adaptive icons)
  if (brand.logos.maskable) {
    icons.push({
      src: brand.logos.maskable,
      sizes: '512x512',
      type: 'image/png',
      purpose: 'maskable',
    });
  }

  // If no maskable, use the 512 icon as maskable too
  if (!brand.logos.maskable && brand.logos.icon512) {
    icons.push({
      src: brand.logos.icon512,
      sizes: '512x512',
      type: 'image/png',
      purpose: 'maskable',
    });
  }

  return {
    name: brand.name,
    short_name: brand.shortName,
    description: brand.tagline,
    start_url: '/',
    display: 'standalone',
    orientation: 'portrait',
    background_color: brand.backgroundColor,
    theme_color: brand.themeColor,
    icons,
    categories: ['education', 'health', 'lifestyle'],
    lang: 'en',
    dir: 'ltr',
    scope: '/',
    id: `/${tenant.slug}/`,
  };
}

/**
 * Generate manifest JSON string
 */
export function generateManifestJSON(tenant: TenantConfig): string {
  const manifest = generateManifest(tenant);
  return JSON.stringify(manifest, null, 2);
}

// ============================================
// MANIFEST URL GENERATION
// ============================================

/**
 * Generate a cache-busted manifest URL
 */
export function getManifestURL(tenant: TenantConfig): string {
  // Use tenant updatedAt as cache buster
  const cacheKey = new Date(tenant.updatedAt).getTime();
  return `/manifest.json?tenant=${tenant.slug}&v=${cacheKey}`;
}

/**
 * Generate a data URL for the manifest
 * Useful when you can't serve dynamic JSON from the server
 */
export function getManifestDataURL(tenant: TenantConfig): string {
  const json = generateManifestJSON(tenant);
  const base64 = btoa(json);
  return `data:application/manifest+json;base64,${base64}`;
}

// ============================================
// MANIFEST INJECTION
// ============================================

/**
 * Inject or update the manifest link in the document head
 */
export function injectManifestLink(tenant: TenantConfig): void {
  // Check for existing manifest link
  let manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');

  if (!manifestLink) {
    // Create new link element
    manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    document.head.appendChild(manifestLink);
  }

  // For the default tenant, use the static manifest.json file
  // This avoids data URL issues with relative paths and 401 errors
  if (tenant.slug === 'default' || tenant.id === 'default') {
    manifestLink.href = '/manifest.json';
    return;
  }

  // For custom tenants, use data URL to avoid server dependency
  manifestLink.href = getManifestDataURL(tenant);
}

/**
 * Update Apple-specific meta tags for iOS PWA support
 */
export function updateAppleMeta(brand: BrandConfig): void {
  // Apple touch icon
  let appleTouchIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (!appleTouchIcon) {
    appleTouchIcon = document.createElement('link');
    appleTouchIcon.rel = 'apple-touch-icon';
    document.head.appendChild(appleTouchIcon);
  }
  appleTouchIcon.href = brand.logos.icon192 || brand.logos.icon;

  // Apple mobile web app capable
  let webAppCapable = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
  if (!webAppCapable) {
    webAppCapable = document.createElement('meta');
    webAppCapable.setAttribute('name', 'apple-mobile-web-app-capable');
    webAppCapable.setAttribute('content', 'yes');
    document.head.appendChild(webAppCapable);
  }

  // Apple status bar style
  let statusBarStyle = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  if (!statusBarStyle) {
    statusBarStyle = document.createElement('meta');
    statusBarStyle.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
    statusBarStyle.setAttribute('content', 'black-translucent');
    document.head.appendChild(statusBarStyle);
  }

  // Apple title
  let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (!appleTitle) {
    appleTitle = document.createElement('meta');
    appleTitle.setAttribute('name', 'apple-mobile-web-app-title');
    document.head.appendChild(appleTitle);
  }
  appleTitle.setAttribute('content', brand.shortName);
}

/**
 * Full PWA metadata injection
 */
export function injectPWAMeta(tenant: TenantConfig): void {
  injectManifestLink(tenant);
  updateAppleMeta(tenant.brand);

  // Update theme-color meta
  let themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!themeColorMeta) {
    themeColorMeta = document.createElement('meta');
    themeColorMeta.name = 'theme-color';
    document.head.appendChild(themeColorMeta);
  }
  themeColorMeta.content = tenant.brand.themeColor;

  // Update MS tile color
  let msTileColor = document.querySelector<HTMLMetaElement>('meta[name="msapplication-TileColor"]');
  if (!msTileColor) {
    msTileColor = document.createElement('meta');
    msTileColor.name = 'msapplication-TileColor';
    document.head.appendChild(msTileColor);
  }
  msTileColor.content = tenant.brand.themeColor;
}

// ============================================
// SERVICE WORKER CACHE VERSIONING
// ============================================

/**
 * Generate a cache version key based on tenant config
 * Used for service worker cache management
 */
export function getCacheVersion(tenant: TenantConfig): string {
  return `${tenant.slug}-${new Date(tenant.updatedAt).getTime()}`;
}

/**
 * Message service worker to update cache version
 */
export async function notifyServiceWorkerOfTenantChange(tenant: TenantConfig): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;

  if (registration.active) {
    registration.active.postMessage({
      type: 'TENANT_CHANGED',
      payload: {
        tenantId: tenant.id,
        cacheVersion: getCacheVersion(tenant),
        themeColor: tenant.brand.themeColor,
      },
    });
  }
}
